import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import InchargeLayout from './InchargeLayout';
import {
    FaSearch,
    FaFileAlt,
    FaAddressCard,
    FaUserCircle,
    FaTrashAlt,
    FaUpload,
    FaCopy,
    FaFileExcel,
    FaPrint,
    FaChevronRight,
    FaArrowLeft
} from 'react-icons/fa';

const ViewSubheForm = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const registrationsRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubData = onValue(registrationsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                // Real-time data from Firebase, sorted newest first
                setRegistrations(list.reverse());
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const filteredData = registrations.filter(item => {
        // Only show users who are NOT yet Assistant Approved in this "New" list
        const status = (item.assistantStatus || '').toLowerCase();
        if (status === 'approved' || status === 'accepted') return false;

        const fullName = `${item.firstName || ''} ${item.middleName || ''} ${item.surname || ''}`.trim().toLowerCase();
        const phone = (item.whatsappNo || '').toString();
        return fullName.includes(searchTerm.toLowerCase()) || phone.includes(searchTerm);
    });

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const StatusBadge = ({ status }) => {
        const statusLower = (status || 'pending').toLowerCase();
        let colorClass = 'text-amber-600 bg-amber-50 border-amber-200';
        let text = 'Pending';

        if (statusLower === 'accepted' || statusLower === 'approved') {
            colorClass = 'text-green-700 bg-green-50 border-green-200';
            text = 'Approved';
        } else if (statusLower === 'rejected') {
            colorClass = 'text-red-700 bg-red-50 border-red-200';
            text = 'Rejected';
        }

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
                {text}
            </span>
        );
    };

    return (
        <InchargeLayout>
            <div className="min-h-screen bg-gray-50 p-4 md:p-6 text-gray-900">
                {/* Header & Breadcrumb */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 bg-white border border-gray-300 rounded-lg text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all shadow-sm"
                        >
                            <FaArrowLeft size={14} />
                        </button>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-800">नवीन सु.बे. अभियंत्यांची यादी</h1>
                        </div>
                    </div>
                    <div className="flex items-center text-xs font-medium text-gray-500 gap-2">
                        <span>सांगली जिल्हा परिषद</span>
                        <FaChevronRight size={8} className="text-gray-400" />
                        <span className="text-blue-600">नवीन सु.बे. अभियांत्रिकी यादी</span>
                    </div>
                </div>

                {/* Main Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

                    {/* Legend Section */}
                    <div className="px-6 pt-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                                <span className="text-xs text-gray-600">प्रलंबित (Pending)</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-green-600"></div>
                                <span className="text-xs text-gray-600">मंजूर (Approved)</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-600"></div>
                                <span className="text-xs text-gray-600">नाकारले (Rejected)</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5"><FaAddressCard className="text-gray-600" /> माहिती</div>
                            <div className="flex items-center gap-1.5"><FaUserCircle className="text-gray-600" /> कागदपत्रे</div>
                            <div className="flex items-center gap-1.5"><FaTrashAlt className="text-gray-400" /> डिलीट</div>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-4 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded text-xs font-medium hover:bg-gray-200 transition">
                                <FaCopy size={12} /> COPY
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded text-xs font-medium hover:bg-gray-200 transition">
                                <FaFileExcel size={12} /> EXCEL
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded text-xs font-medium hover:bg-gray-200 transition">
                                <FaPrint size={12} /> PRINT
                            </button>
                        </div>
                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <label className="text-xs font-medium text-gray-600">Search:</label>
                            <input
                                type="text"
                                className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all w-full md:w-64"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="नाव किंवा फोन नंबर शोधा..."
                            />
                        </div>
                    </div>

                    {/* Table Container */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 text-xs font-semibold border-b border-gray-200">
                                    <th className="px-4 py-4 text-center w-12 border-r border-gray-100">क्र.</th>
                                    <th className="px-4 py-4 border-r border-gray-100 min-w-[180px]">अर्जदाराचे नाव</th>
                                    <th className="px-4 py-4 border-r border-gray-100 text-center">फोन क्र.</th>
                                    <th className="px-4 py-4 border-r border-gray-100 text-center">नोंदणी दिनांक</th>
                                    <th className="px-4 py-4 border-r border-gray-100 text-center">Assistant</th>
                                    <th className="px-4 py-4 border-r border-gray-100 text-center">Accountant</th>
                                    <th className="px-4 py-4 border-r border-gray-100 text-center">Dept. Eng</th>
                                    <th className="px-4 py-4 border-r border-gray-100 text-center">Engineer</th>
                                    <th className="px-4 py-4 border-r border-gray-100 text-center">Add CEO</th>
                                    <th className="px-4 py-4 border-r border-gray-100 text-center">CEO</th>
                                    <th className="px-4 py-4 text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="11" className="px-4 py-10 text-center text-gray-400">माहिती लोड होत आहे...</td>
                                    </tr>
                                ) : (
                                    filteredData.map((item, index) => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4 text-center text-sm text-gray-500 border-r border-gray-50">
                                                {index + 1}
                                            </td>
                                            <td className="px-4 py-4 text-sm font-medium text-gray-800 border-r border-gray-50">
                                                {item.surname} {item.firstName} {item.middleName}
                                            </td>
                                            <td className="px-4 py-4 text-center text-sm text-gray-600 border-r border-gray-50">
                                                {item.whatsappNo}
                                            </td>
                                            <td className="px-4 py-4 text-center border-r border-gray-50">
                                                <span className="text-xs text-gray-600">{formatDate(item.submittedAt)}</span>
                                            </td>
                                            <td className="px-4 py-4 text-center border-r border-gray-50"><StatusBadge status={item.assistantStatus} /></td>
                                            <td className="px-4 py-4 text-center border-r border-gray-50"><StatusBadge status={item.payments?.["Registration invoice"]?.accountantStatus} /></td>
                                            <td className="px-4 py-4 text-center border-r border-gray-50"><StatusBadge status={item.deputyEngStatus} /></td>
                                            <td className="px-4 py-4 text-center border-r border-gray-50"><StatusBadge status={item.engineerStatus} /></td>
                                            <td className="px-4 py-4 text-center border-r border-gray-50"><StatusBadge status={item.addCeoStatus} /></td>
                                            <td className="px-4 py-4 text-center border-r border-gray-50"><StatusBadge status={item.ceoStatus} /></td>

                                            <td className="px-4 py-4 text-center">
                                                <div className="flex items-center justify-center gap-4">
                                                    <button
                                                        onClick={() => navigate('/main-view-application-form', { state: { userData: item } })}
                                                        className="text-gray-600 hover:text-blue-600 transition-colors"
                                                        title="तपशील पहा"
                                                    >
                                                        <FaAddressCard size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => navigate('/main-new-subhe-status', { state: { userData: item } })}
                                                        className="text-gray-600 hover:text-blue-600 transition-colors"
                                                        title="स्थिती पहा"
                                                    >
                                                        <FaUserCircle size={16} />
                                                    </button>
                                                    <button className="text-gray-300 hover:text-red-500 transition-colors" title="डिलीट">
                                                        <FaTrashAlt size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="px-6 py-6 bg-gray-50 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-xs text-gray-500">
                            Showing <span className="font-semibold text-gray-700">1</span> to <span className="font-semibold text-gray-700">{filteredData.length}</span> of <span className="font-semibold text-gray-700">{registrations.length}</span> entries
                        </p>
                        <div className="flex items-center gap-2">
                            <button className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-600 hover:bg-white transition">Previous</button>
                            <button className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium shadow-sm">1</button>
                            <button className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-600 hover:bg-white transition">Next</button>
                        </div>
                    </div>
                </div>

                {/* Footer Copyright */}
                <div className="mt-8 text-center pb-8">
                    <p className="text-[11px] text-gray-400 font-medium">
                        © 2026 सांगली जिल्हा परिषद. सर्व हक्क राखीव.
                    </p>
                </div>
            </div>
        </InchargeLayout>
    );
};

export default ViewSubheForm;
